import { LocalizationString } from '@celito.clients/assets';
import { TrainingPlanStatusEnum } from '@celito.clients/enums';
import {
  useCallbackPrompt,
  useDeepCompareEffect,
  useLayout,
  useURLParams,
} from '@celito.clients/hooks';
import { editFormSubmitApi, formSubmitApi } from '@celito.clients/services';
import {
  ControlledDocumentRequestTypeEnum,
  Field,
  TrainingPlanRequestTypeEnum,
} from '@celito.clients/types';
import { errorToast, formatDateInTimezone } from '@celito.clients/utils';
import { AxiosProgressEvent } from 'axios';
import { FileDataProps } from 'libs/shared/src/lib/multi-file-upload/multi-file-upload.model';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  smartGroupPreviewPropName,
  trainessPreviewPropName,
  trainingRolesPreviewPropName,
} from '../../../custom-pages/assingment-page/config';
import { useFormEngineContext } from '../../../hooks';
import useFormApiErrorHandler from '../../../hooks/use-form-api-error-handler';
import { RecorLevelUserContextFields } from '../../../types/common-types';
import { checkToIncludeRequestor } from '../../../utils/check-requestor';
import {
  atLeastOneValueExists,
  getFormActionsVisibilityRules,
  getFormValues,
  refactorFormData,
} from '../../../utils/helper';
import { WizardContainerComponentProps } from '../wizard-container.model';
import { AddScreenProps } from './add-screen.model';
import AddScreenView from './add-screen.view';

const attachmentsPropName = 'attachments__a';
// for system objects the attachment key name will be attachments__s
const attachmentSystemPropName = 'attachments__s';
const filePropName = 'file__a';

// Decision making component
// This component will be responsible for deciding which UI to render
// based on the current state of the application
export const AddScreenController = (props: WizardContainerComponentProps) => {
  const {
    sections = [],
    attributeConfig: objectDefinition,
    objectName,
    step,
    submitUrl = '',
    totalSteps = 0,
  } = props;
  const { configureLayout } = useLayout();
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isDataSaved, setIsDataSaved] = useState(false);
  const [docUploadProgress, setDocUploadProgress] = useState(0);
  const [isDataSubmitted, setIsDataSubmitted] = useState(false);
  const [recordDetails, setRecordDetails] = useState<Record<
    string,
    unknown
  > | null>(null);
  const { urlParams } = useURLParams();
  const isSubmittedChildRecord = useRef(false);
  // Store latest submitted record for onSubmit callback (avoids async state timing issue)
  const latestSubmittedRecordRef = useRef<Record<string, unknown> | null>(null);
  const { handleFormApiError } = useFormApiErrorHandler();
  const {
    setFieldsApiErrors,
    onCancel: onCancelHandler,
    onSubmit,
    onSave: onSaveHandler,
    fieldsState,
  } = useFormEngineContext();

  const requestType = urlParams.get('requestType');

  const isTrainingPlanRequest =
    requestType === TrainingPlanRequestTypeEnum.NewTrainingPlan;
  const isControlledDocumentRequest =
    requestType === ControlledDocumentRequestTypeEnum.NewDocument;

  const navigate = useNavigate();

  const methods = useFormContext();

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { isDirty, dirtyFields },
    reset,
    getValues,
  } = methods;

  const allSectionsFields = useMemo(() => {
    return sections.reduce((acc, section) => {
      return acc.concat(section.fields);
    }, [] as Field[]);
  }, [sections]);

  const parentObjectRecordName = urlParams.get('pr');

  // Watch specific fields used in the "Record Type + Record ID" pattern (Effectiveness Check)
  const watchedObjectId = watch('object_id__s') as string | undefined;
  const watchedObjectRecordName = watch('object_record_name__s') as
    | string
    | undefined;

  // Watch all values for the reference-field pattern (e.g., Impact Assessment → Change Control)
  // ControlledPickerView sets the reference as an object {name, label} after an API call
  const allFormValues = watch();

  // Pattern 1: explicit parent link fields both present
  const hasExplicitParentFields =
    !!watchedObjectId && !!watchedObjectRecordName;

  // Pattern 2: any form value is an object that contains the pr= string (reference field)
  const hasReferenceParentField =
    !!parentObjectRecordName &&
    Object.values(allFormValues).some(
      (v) =>
        v != null &&
        typeof v === 'object' &&
        Object.values(v as Record<string, unknown>).includes(
          parentObjectRecordName
        )
    );

  const isParentDataReady =
    !parentObjectRecordName ||
    hasExplicitParentFields ||
    hasReferenceParentField;

  useEffect(() => {
    if (requestType) {
      setValue('initiation_type__a', requestType);
      configureLayout({
        pageTitle: '',
        headerTitle: requestType, // TODO: should come from viewDTO
        enablePadding: false,
      });
    }
  }, [requestType]);

  // Fire auto-save once parent data is present in the form (either pattern)
  useEffect(() => {
    if (!parentObjectRecordName) return; // Not a child-record flow
    if (step !== 0) return; // Only on first step
    if (isSubmittedChildRecord.current) return; // Already submitted
    if (!isParentDataReady) return; // Wait for parent data

    isSubmittedChildRecord.current = true;
    const rawData = getValues();
    const formDataObject = {
      ...(isTrainingPlanRequest && {
        request_type__s: 'NewTrainingPlan',
        status__a: TrainingPlanStatusEnum.Draft,
      }),
      ...(isControlledDocumentRequest && {
        initiation_type__a: urlParams.get('requestType'),
      }),
    };

    submitForm(rawData, false, undefined, formDataObject, false, false);
  }, [step, isParentDataReady]);

  useDeepCompareEffect(() => {
    if (recordDetails) {
      const responseValues = getFormValues(objectDefinition, recordDetails);
      const currentFormState = getValues();

      Object.keys(currentFormState).forEach((fieldName) => {
        const currentValue = currentFormState[fieldName];
        const responseValue = responseValues[fieldName];

        if (
          (currentValue === null || currentValue === undefined) &&
          responseValue !== undefined
        ) {
          currentFormState[fieldName] = responseValue;
        }
      });

      reset({
        ...responseValues,
        ...currentFormState,
        file__a:
          responseValues.file__a ?? currentFormState.file__a ?? undefined,
        attachments__a:
          responseValues.attachments__a ??
          currentFormState.attachments__a ??
          undefined,
      });
    }
  }, [getValues, recordDetails]);

  const { showPrompt, confirmNavigation, cancelNavigation } = useCallbackPrompt(
    !!props.showCancelPopup && isDirty
  );

  const toggleWarningModalBtnClick = () => {
    setIsWarningModalOpen(!isWarningModalOpen);
  };

  // Explicit handlers to avoid boolean flag parameters
  const openAndResetSaveModal = () => {
    setIsSaveModalOpen(!isSaveModalOpen);
    reset({ ...getValues(), isDirty: false });
  };

  const resetAfterSaveWithoutModal = () => {
    reset({ ...getValues(), isDirty: false });
  };

  // Keep original name for view props (no boolean parameter)
  const toggleSaveModalBtnClick = () => {
    openAndResetSaveModal();
  };

  const toggleSubmitModalBtnClick = (
    submittedRecord?: Record<string, unknown> | React.MouseEvent
  ) => {
    // Check if argument is a record (not an event) - events have 'type' and 'target' properties
    const isEvent =
      submittedRecord &&
      'type' in submittedRecord &&
      'target' in submittedRecord;

    // If a record is passed (from API), store it in ref for later use
    if (submittedRecord && !isEvent) {
      latestSubmittedRecordRef.current = submittedRecord as Record<
        string,
        unknown
      >;
    }

    setIsSubmitModalOpen(!isSubmitModalOpen);
    reset({ ...getValues(), isDirty: false });

    if (isSubmitModalOpen) {
      // Use ref (always has latest data) or fall back to state
      const record = latestSubmittedRecordRef.current ?? recordDetails;
      if (onSubmit) {
        onSubmit(
          record
            ? {
                name: record.name as string,
                label: record.label as string | undefined,
                version: record.version as string | undefined,
                ...record,
              }
            : undefined
        );
      } else if (submitUrl) {
        navigate(`../${submitUrl}`);
      } else {
        navigate(-1);
      }
    }
  };

  const toggleCancelModalBtnClick = (navigateBack?: boolean) => {
    if (navigateBack) {
      // If confirming navigation away
      reset({ isDirty: false });
      setIsCancelModalOpen(false);
      if (onCancelHandler) {
        onCancelHandler();
      } else {
        confirmNavigation();
        onRouteBack();
      }
    } else {
      // Just toggle the modal state
      setIsCancelModalOpen((prev) => !prev);
    }
  };

  const onCancel: AddScreenProps['onCancel'] = (e) => {
    e.preventDefault();
    if (props.showCancelPopup && isDirty) {
      setIsCancelModalOpen(true);
    } else {
      onCancelHandler ? onCancelHandler() : onRouteBack();
    }
  };

  const onRouteBack = () => {
    navigate(-1);
  };

  const onFormSubmit = (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    handleSubmit(() => onSave(false));
  };

  const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
    const progress = progressEvent.progress ?? 0;
    const totalBytes = progressEvent.total ?? 0;

    // Only show progress bar for files larger than 10MB
    const MIN_FILE_SIZE_FOR_PROGRESS = 10 * 1024 * 1024; // 10MB in bytes

    if (totalBytes < MIN_FILE_SIZE_FOR_PROGRESS) {
      return; // Don't show progress bar for small files
    }

    // Keep progress at 95% when upload completes to show server is still processing
    if (progress === 1) {
      setDocUploadProgress(95);
    } else {
      // Scale progress to max 90% during upload
      setDocUploadProgress(progress * 90);
    }
  };

  const callFormApi = async (
    payload: FormData,
    isSubmit: boolean,
    extraQueryString: Record<string, unknown> = {},
    fromNextOrPrevious = false,
    showSaveModal = true
  ) => {
    isSubmit ? setIsDataSubmitted(true) : setIsDataSaved(true);
    try {
      const submitFlag = extraQueryString?.isSubmit;

      if (submitFlag) {
        delete extraQueryString.isSubmit;
      }
      const params = {
        ...(isSubmit && { isSubmit: true }),
        ...extraQueryString,
      };

      const response = await formSubmitApi(objectName, payload, params, {
        onUploadProgress,
      });

      if (response.record.name) {
        setRecordDetails({
          ...response.record,
        });
      }
      confirmNavigation();

      if (isSubmit) {
        // Pass record directly to avoid async state timing issue
        toggleSubmitModalBtnClick(response.record);
      } else if (fromNextOrPrevious) {
        // Intentionally no-op when navigating between steps
      } else if (onSaveHandler) {
        // Inline creation mode: notify parent (e.g., reference-selector) with record data
        onSaveHandler(response.record);
      } else {
        showSaveModal ? openAndResetSaveModal() : resetAfterSaveWithoutModal();
      }
      setFieldsApiErrors?.({
        fields: [],
        sections: [],
      });
    } catch (_error) {
      handleFormApiError(_error, sections);
    } finally {
      // Always reset progress bar in finally block
      setDocUploadProgress(0);
      isSubmit ? setIsDataSubmitted(false) : setIsDataSaved(false);
    }
  };

  const callEditFormApi = async (
    payload: FormData,
    isSubmit: boolean,
    extraQueryString: Record<string, unknown> = {},
    fromNextOrPrevious = false
  ) => {
    isSubmit ? setIsDataSubmitted(true) : setIsDataSaved(true);
    try {
      const submitFlag = extraQueryString?.isSubmit;

      if (submitFlag) {
        delete extraQueryString.isSubmit;
      }
      const params: Record<string, unknown> = {
        ...(isSubmit && { isSubmit: true }),
        ...(props.labelFormat && { recordName: payload?.get('name__s') }),

        ...extraQueryString,
      };

      if (
        objectDefinition?.isVersioningEnabled &&
        !extraQueryString.skipVersionUpgrade
      ) {
        params.skipVersionUpgrade =
          (Object.keys(dirtyFields).length >= 1 &&
            recordDetails?.isActive &&
            !isSubmit) ||
          false;
      }

      if (params.skipVersionUpgrade && recordDetails?.version) {
        params.version = recordDetails.version;
      }

      if (props.labelFormat) {
        payload.delete('name__s');
        payload.delete('label__s');
      }

      const response = await editFormSubmitApi(
        objectName,
        recordDetails?.name as string,
        payload,
        params,
        {
          onUploadProgress,
        }
      );

      setValue(`deleted_${attachmentsPropName}`, '');
      setValue(`deleted_${attachmentSystemPropName}`, '');
      setValue(`deleted_${filePropName}`, []);

      if (response.record.name && response.record.version) {
        setRecordDetails({
          ...response.record,
        });
      }

      confirmNavigation();

      if (isSubmit) {
        toggleSubmitModalBtnClick(response.record);
      } else if (fromNextOrPrevious) {
        resetAfterSaveWithoutModal();
      } else if (onSaveHandler) {
        // Inline creation mode: notify parent (e.g., reference-selector) with record data
        onSaveHandler(response.record);
      } else {
        openAndResetSaveModal();
      }
      setFieldsApiErrors?.({
        fields: [],
        sections: [],
      });
    } catch (_error) {
      handleFormApiError(_error, sections);
    } finally {
      // Always reset progress bar in finally block
      setDocUploadProgress(0);
      isSubmit ? setIsDataSubmitted(false) : setIsDataSaved(false);
    }
  };

  const submitForm = async (
    rawData: FieldValues,
    isSubmit: boolean,
    extraQueryString: Record<string, unknown> = {},
    formDataObject: Record<string, unknown> = {},
    fromNextOrPrevious = false,
    showSaveModal = true
  ) => {
    if (!rawData.completion_type__a) {
      delete rawData.completion_type__a;
    }
    let data = refactorFormData(
      objectDefinition,
      rawData,
      fieldsState as Record<
        string,
        { isVisible?: boolean; isEditable?: boolean }
      >
    );
    if (Object.keys(formDataObject).length > 0) {
      data = { ...data, ...formDataObject };
    }

    // Set non-visible fields to null based on layout rules
    if (fieldsState && typeof fieldsState === 'object') {
      Object.keys(data).forEach((key) => {
        const state = (
          fieldsState as Record<
            string,
            { isVisible?: boolean; isHidden?: boolean }
          >
        )[key];
        data[key] = state?.isVisible === false ? null : data[key];
      });
    }

    if (!data?.file__a?.documentId) {
      data.file__a = watch('file__a');
    }

    // for control docs
    const attachments =
      watch(attachmentsPropName) || watch(attachmentSystemPropName);
    if (attachments?.length > 0) {
      data.secondary_file__a = recordDetails?.name
        ? attachments.filter(
            // only File type should be passed to secondary_file__a and will act as append in the DB
            // in EDIT mode. When record is already created, the screen should be treated as EDIT mode
            (attachment: File | FileDataProps) => attachment instanceof File
          )
        : [...attachments];
    }

    let markedForDeleteAttachments: string[] = [];
    let documentDeleted = false;

    if (recordDetails?.name) {
      // if recordDetails exists, record has been created and form is treated as EDIT mode
      markedForDeleteAttachments =
        watch(`deleted_${attachmentsPropName}`) ??
        watch(`deleted_${attachmentSystemPropName}`) ??
        [];
      const markedForDeleteFile = watch(`deleted_${filePropName}`) ?? [];

      if (markedForDeleteFile?.length) {
        documentDeleted = true;
      }

      delete data?.version__s;
    }

    delete data?.[attachmentsPropName];
    delete data?.[`deleted_${attachmentsPropName}`];
    delete data?.[attachmentSystemPropName];
    delete data?.[`deleted_${attachmentSystemPropName}`];
    delete data?.[`deleted_${filePropName}`];
    delete data?.[trainessPreviewPropName];
    delete data?.[smartGroupPreviewPropName];
    delete data?.[trainingRolesPreviewPropName];
    delete data?.['isDirty'];
    delete data?.['name__s'];
    delete data?.['label__s'];
    delete data?.['is_trainable__a'];
    delete data?.['lifecycle_stage__s'];
    delete data?.['document_status__a'];
    delete data?.['currentQuestion'];
    delete data?.['isEditMode'];
    delete data?.['modified_by_user__s'];
    delete data?.['created_by_user__s'];
    delete data?.['created_at_utc__s'];
    delete data?.['modified_at_utc__s'];
    delete data?.['uuid__s'];
    delete data?.[RecorLevelUserContextFields.RECORD_AND_SYSTEM_ROLES];

    if (!checkToIncludeRequestor(objectDefinition.name))
      delete data?.['requestor__a'];

    if (objectDefinition?.name === 'quiz__a') {
      delete data?.['initiation_type__a'];
    }

    const formData = new FormData();

    if (documentDeleted) {
      // tells BE that the file has been deleted
      formData.append(`documentDeleted`, 'true');
    }

    if (markedForDeleteAttachments.length > 0) {
      formData.append(
        'deletedDocumentIds',
        JSON.stringify(markedForDeleteAttachments)
      );
    }

    Object.keys(data).forEach((key) => {
      if (
        data[key] !== undefined &&
        !(data[key] instanceof Date) &&
        data[key] !== '' &&
        !Number.isNaN(data[key])
      ) {
        // for multi file uploads
        if (Array.isArray(data[key]) && key === 'secondary_file__a') {
          for (let i = 0; i < data[key].length; i++) {
            formData.append(key, data[key][i]);
          }
          return;
        }

        formData.append(
          key,
          Array.isArray(data[key])
            ? data[key].length > 0
              ? JSON.stringify(data[key])
              : null
            : data[key]
        );
      }
      if (data[key] instanceof Date) {
        formData.append(key, formatDateInTimezone(data[key]));
      }
    });

    // call the Edit API if the record is already created
    if (recordDetails?.name) {
      callEditFormApi(
        formData,
        isSubmit || (extraQueryString?.isSubmit as boolean),
        {
          ...extraQueryString,
        },
        fromNextOrPrevious
      );
      return;
    }

    callFormApi(
      formData,
      isSubmit || (extraQueryString?.isSubmit as boolean),
      extraQueryString,
      fromNextOrPrevious,
      showSaveModal
    );
  };

  const onValidSubmitForm = (
    rawData: FieldValues,
    isSubmit: boolean,
    extraQueryString: Record<string, unknown> = {},
    formDataObject: Record<string, unknown> = {},
    fromNextOrPrevious: boolean
  ) => {
    const oneValueExists = atLeastOneValueExists(rawData, allSectionsFields);

    if (!fromNextOrPrevious && !oneValueExists) {
      errorToast({
        message: LocalizationString.VALIDATION_DESCRIPTION,
      });
      return;
    }

    if (oneValueExists) {
      submitForm(
        rawData,
        isSubmit,
        extraQueryString,
        formDataObject,
        fromNextOrPrevious
      );
    }
  };

  const onInvalidSubmitForm = (
    isSubmit: boolean,
    extraQueryString: Record<string, unknown> = {},
    formDataObject: Record<string, unknown> = {},
    fromNextOrPrevious = false
  ) => {
    const values = getValues();
    const oneValueExists = atLeastOneValueExists(values, allSectionsFields);

    if (isSubmit || (!fromNextOrPrevious && !oneValueExists)) {
      errorToast({
        message: LocalizationString.VALIDATION_DESCRIPTION,
      });
      return;
    }

    if (oneValueExists) {
      submitForm(
        values,
        isSubmit,
        extraQueryString,
        formDataObject,
        fromNextOrPrevious
      );
    }
  };

  const onSave = async (
    isSubmit = false,
    extraQueryString: Record<string, unknown> = {},
    formDataObject: Record<string, unknown> = {},
    fromNextOrPrevious = false,
    validateCurrentSection = false
  ) => {
    if (!isSubmit) {
      // If validateCurrentSection is true and record is submitted, validate current section fields
      if (
        validateCurrentSection &&
        !fromNextOrPrevious &&
        recordDetails?.isSubmitted
      ) {
        const currentStepFields = (sections?.[step]?.fields ?? []).map(
          (field) => field.columnName
        );

        // validate only current section fields
        const isValid = await trigger(currentStepFields);

        if (!isValid) {
          // Show validation error toast
          errorToast({
            message: LocalizationString.VALIDATION_DESCRIPTION,
          });
          return;
        }
      }

      const formValues = getValues();
      return onValidSubmitForm(
        formValues,
        isSubmit,
        extraQueryString,
        formDataObject,
        fromNextOrPrevious
      );
    }

    await handleSubmit(
      (rawData) =>
        onValidSubmitForm(
          rawData,
          isSubmit,
          extraQueryString,
          formDataObject,
          fromNextOrPrevious
        ),
      (_err) =>
        onInvalidSubmitForm(
          isSubmit,
          extraQueryString,
          formDataObject,
          fromNextOrPrevious
        )
    )();
  };

  const handleSaveWithValidation = async () => {
    // Validate current section fields before saving if record is submitted
    await onSave(
      false,
      undefined,
      {
        ...(isTrainingPlanRequest && {
          request_type__s: 'NewTrainingPlan',
          status__a: TrainingPlanStatusEnum.Draft,
        }),
        ...(isControlledDocumentRequest && {
          initiation_type__a: urlParams.get('requestType'),
        }),
      },
      false,
      true // validateCurrentSection = true
    );
  };

  const handleGoToNextStep = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (sections?.[step]?.pageComponent) {
      props.goToNextStep(e);
      return;
    }

    if (step <= totalSteps - 1) {
      // validate the current step before advancing
      // for custom screens, validation is done at the custom screen level
      const currentStepFields = (sections?.[step]?.fields ?? []).map(
        (field) => field.columnName
      );

      // validate only visible fields in current section
      const isValid = await trigger(currentStepFields);

      props.setWizardStepsState((prev) =>
        prev.map((prevStep) =>
          prevStep.label === sections?.[step]?.label
            ? { ...prevStep, isValid }
            : prevStep
        )
      );

      if (!isValid) {
        return;
      }

      await onSave(
        false,
        {},
        {
          ...(isTrainingPlanRequest && {
            request_type__s: 'NewTrainingPlan',
            status__a: TrainingPlanStatusEnum.Draft,
          }),
          ...(isControlledDocumentRequest && {
            initiation_type__a: requestType,
          }),
        },
        true
      );

      props.goToNextStep(e);
    }
  };

  const { showSaveButton } = getFormActionsVisibilityRules(
    objectDefinition?.lifecycleStageGroups,
    'Unsubmitted',
    'edit'
  );

  return (
    <AddScreenView
      {...props}
      {...{
        onCancel,
        attributeConfig: objectDefinition,
        control,
        onSave,
        onSaveWithValidation: handleSaveWithValidation,
        handleSubmit,
        onFormSubmit,
        watch,
        setValue,
        trigger,
        step,
        sections,
        submitUrl,
        totalSteps,
        isSaveModalOpen,
        isWarningModalOpen,
        isSubmitModalOpen,
        toggleWarningModalBtnClick,
        toggleSaveModalBtnClick,
        toggleSubmitModalBtnClick,
        isCancelModalOpen,
        mode: props.mode,
        toggleCancelModalBtnClick,
        methods,
        isLoading: isDataSaved,
        isSubmitting: isDataSubmitted,
        isParentDataReady,
        cancelNavigation,
        confirmNavigation,
        showPrompt,
        goToNextStep: handleGoToNextStep,
        docUploadProgress,
        showSaveButton,
      }}
    />
  );
};
